import { Alert, Button, Form, Input, Space } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useAuthenticatedRequest } from '../../UserContext';
import {
  ConferenceApiError,
  ConferenceReport,
  conferenceRequest,
  ReportResponse,
  Submission,
} from './api';
import { useRetryWait } from './delete-report';

/** Validate saved work under the same ID, requesting a key only for a blocker. */
export function ResumeReportAction({
  apiUrl,
  report,
  onReport,
}: {
  apiUrl: string;
  report: ConferenceReport;
  onReport: (response: ReportResponse) => void;
}): JSX.Element | null {
  const authenticatedRequest = useAuthenticatedRequest();
  const [form] = Form.useForm<Pick<Submission, 'api_key'>>();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [waiting, wait] = useRetryWait();
  const pending = useRef<AbortController>();
  const stopped =
    ['failed', 'partial', 'completed'].includes(report.status) &&
    !['sent', 'sending'].includes(report.delivery || '');
  const validating = report.recovery_state === 'validating';
  const canResume = stopped && (report.recovery_eligible || validating);
  const needsKey =
    canResume &&
    report.recovery_state === 'blocked' &&
    report.recovery_needs_api_key;
  useEffect(() => () => pending.current?.abort(), []);
  useEffect(() => {
    if (canResume) {
      form.resetFields();
    }
  }, [form, canResume, needsKey]);

  const resume = async (values: Pick<Submission, 'api_key'>) => {
    if (pending.current || waiting || !canResume) {
      return;
    }
    const controller = new AbortController();
    pending.current = controller;
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await conferenceRequest(
        apiUrl,
        authenticatedRequest,
        'RESUME',
        controller.signal,
        report.report_id,
        needsKey ? { api_key: values.api_key.trim() } : undefined,
      );
      if (!controller.signal.aborted && response) {
        onReport(response);
      }
    } catch (error: unknown) {
      if (!controller.signal.aborted) {
        const failure =
          error instanceof ConferenceApiError
            ? error
            : new ConferenceApiError(0, 'RESUME');
        setError(failure.message);
        if (failure.retryAfterMs || [409, 503].includes(failure.status)) {
          wait(failure.retryAfterMs ?? 10_000);
        }
      }
    } finally {
      pending.current = undefined;
      form.resetFields();
      if (!controller.signal.aborted) {
        setSubmitting(false);
      }
    }
  };

  if (!stopped || (!canResume && !report.recovery_blocker)) {
    return null;
  }
  const blockers: Record<string, string> = {
    invalid_settings:
      'Recovery is blocked: saved settings need engineering repair before retrying.',
    missing_or_corrupt_artifacts:
      'Recovery is blocked: saved data is missing or corrupt. Inspect and repair the artifacts before retrying; retrying cannot recreate deleted data.',
    incomplete_report_json:
      'Recovery is blocked: the saved report is incomplete and needs repair before retrying.',
    api_key_required:
      'Recovery is blocked: the saved credential is unavailable or invalid. Enter a replacement FUB API key from the original account.',
    credential_account_mismatch:
      'Recovery is blocked: the credential belongs to a different FUB account. Enter a replacement key from the original account.',
    credential_identity_unavailable:
      'Recovery is blocked: the FUB account could not be verified. Retry with a replacement key from the original account.',
    recovery_dependency_unavailable:
      'Recovery is blocked: a required service is unavailable. Retry recovery after the service recovers.',
  };

  return (
    <Space direction="vertical" style={{ display: 'flex', marginBottom: 16 }}>
      {validating ? (
        <Alert
          message="Validating recovery… Saved work and credentials are being checked; processing has not resumed yet. If validation was interrupted, use Retry recovery."
          showIcon
          type="info"
        />
      ) : (
        (report.recovery_state === 'blocked' || report.recovery_blocker) && (
          <Alert
            message={
              blockers[report.recovery_blocker || ''] ||
              'Recovery is blocked. Contact the Insights administrator before retrying.'
            }
            showIcon
            type="warning"
          />
        )
      )}
      {error && <Alert message={error} showIcon type="error" />}
      {canResume && (
        <Form
          form={form}
          layout="vertical"
          onFinish={values => void resume(values)}
          preserve={false}
        >
          {needsKey && (
            <Form.Item
              label="Replacement FUB API key"
              name="api_key"
              rules={[
                {
                  transform: (value: string) => value?.trim(),
                  required: true,
                  max: 1024,
                  message:
                    'Enter a nonblank FUB API key of at most 1,024 characters.',
                },
              ]}
            >
              <Input.Password
                autoComplete="off"
                disabled={submitting || waiting}
                visibilityToggle={false}
              />
            </Form.Item>
          )}
          <Button
            disabled={submitting || waiting}
            htmlType="submit"
            loading={submitting}
            type="primary"
          >
            {waiting
              ? 'Wait before retrying recovery'
              : validating
                ? 'Retry recovery'
                : 'Resume report'}
          </Button>
        </Form>
      )}
    </Space>
  );
}
